import type { GetServerSidePropsContext } from 'next';

import {
  CurrencyProfilePageLayout,
  CurrencyProfilePageLayoutProps,
} from '@/components';
import { GenericPageLayout } from '@/components/layout/layout.page.component';
import {
  getCurrenciesPageCorridors,
  getCurrencyDetailsFromSlug,
  getCurrencyInformationByCode,
} from '@/services';
import { getCurrencyConverterPageData, interpolate, logger } from '@/utils';

export const getServerSideProps = async (
  initialProps: GetServerSidePropsContext,
) => {
  try {
    const {
      currencyCode,
      currencyName,
      countryCode,
    } = getCurrencyDetailsFromSlug(initialProps?.params?.slug as string);

    let resolvedProps = await getCurrencyConverterPageData(initialProps);

    const currencyInformation = await getCurrencyInformationByCode({
      currencyCode,
      currencyName,
      countryCode,
      preview: initialProps.preview,
      locale: initialProps.locale,
    });

    // Return 404 if not a valid currency
    if (
      !currencyInformation?.currencyCode ||
      !currencyInformation?.countryCode
    ) {
      return { notFound: true };
    }

    if (currencyInformation?.currencyCode && resolvedProps.notFound) {
      resolvedProps = await getCurrencyConverterPageData({
        ...initialProps,
        overrideProps: {
          slug: '/currency-converter/currencies/generic',
        },
      });
    }

    const currencyCorridors = await getCurrenciesPageCorridors({
      countryCode: currencyInformation.countryCode.toLowerCase(),
      locale: initialProps?.locale,
    });

    // Fetch currency information using default slug (currency-converter/gbp)
    if (resolvedProps?.props) {
      // Interpolate title, description, and breadcrumbs on the server side
      const interpolatedTitle = interpolate(resolvedProps.props.title, {
        currencyName: currencyInformation.currencyName ?? '',
        currencyCode: currencyInformation.currencyCode ?? '',
      });

      const interpolatedDescription = resolvedProps.props.description
        ? interpolate(resolvedProps.props.description, {
            currencyName: currencyInformation.currencyName ?? '',
            currencyCode: currencyInformation.currencyCode ?? '',
          })
        : '';

      const interpolatedBreadcrumbs =
        resolvedProps.props.breadcrumbs?.map(
          (breadcrumb: { name: string | null; slug: string | null }) => ({
            ...breadcrumb,
            name:
              interpolate(breadcrumb.name, {
                currencyName: currencyInformation.currencyName ?? '',
                currencyCode: currencyInformation.currencyCode ?? '',
              }) ?? null,
          }),
        ) ?? null;

      const interpolatedAnalyticsPageName = interpolate(
        resolvedProps.props.analyticsPageName,
        {
          currencyName: currencyInformation.currencyName ?? '',
          currencyCode: currencyInformation.currencyCode ?? '',
        },
      );

      return {
        props: {
          ...resolvedProps.props,
          title: interpolatedTitle,
          description: interpolatedDescription,
          breadcrumbs: interpolatedBreadcrumbs,
          analyticsPageName: interpolatedAnalyticsPageName,
          currencyInformation,
          currencyCorridors,
        },
      };
    }
  } catch (error) {
    logger.error({ error }, 'Error in getServerSideProps for CurrenciesPage');
    return {
      notFound: true,
    };
  }
};

const CurrenciesPage = (props: CurrencyProfilePageLayoutProps) => {
  return (
    <GenericPageLayout {...props}>
      <CurrencyProfilePageLayout {...props} />
    </GenericPageLayout>
  );
};

export default CurrenciesPage;
